import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import FeatureBlock from '@/components/feature-block'
import FincircleForm from '@/components/fincircle-form'
import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {FincirclePageQuery} from '@/graphql-types'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Section} from '@helios/shared/styles/grid'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import {LifecycleStages} from '@helios/shared/types/marketing-campaigns'

type Props = PageProps<FincirclePageQuery>

const Fincircle = (props: Props) => {
    const content = props.data.allPrismicFincirclePage.nodes[0].data
    const {invalid_email_error} = useGlobalStrings()

    if (!content) {
        return handleNoPrismicData()
    }

    const hero = content.hero[0]

    return (
        <>
            <Header />

            <Main>
                <Section>
                    <FeatureBlock {...{...hero, body: hero.body.richText}}>
                        <FincircleForm
                            inputPlaceholder={
                                hero.signup_banner.document?.data.email_input_placeholder
                            }
                            submitText={hero.submit_text}
                            emailInputErrorText={invalid_email_error}
                            consentErrorText={hero.consent_error_text}
                            hubspotProperties={{
                                lifecyclestage: LifecycleStages.Lead,
                                campaign__updating_: 'fincircle_dk',
                            }}
                        />
                    </FeatureBlock>
                </Section>
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<FincirclePageQuery>) => {
    const pageData = data.allPrismicFincirclePage.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

export const query = graphql`
    query FincirclePage($locale: String!) {
        allPrismicFincirclePage(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    hero {
                        title
                        body {
                            richText
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        submit_text
                        consent_error_text
                        signup_banner {
                            document {
                                ...SignupBanner
                            }
                        }
                    }
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(Fincircle)
